import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { isValidDanishPostalCode, useCitySearch } from '~/shared/hooks/useCitySearch';
import { useTranslation } from '~/shared/utils/translation';
import type { Coordinates } from '~/shared/utils';

const SEARCH_DEBOUNCE_MS = 300;

// The origin to sort a store list around. Only searches carry the submitted query.
export type StoreLocation = {
    coordinates: Coordinates;
    source: 'search' | 'geo';
    query?: string;
};

type UseStoreLocationSearchInput = {
    onLocationResolved?: (location: StoreLocation) => void;
};

/**
 * Store search box and "use my location" toggle. Resolves either one to a single active location.
 * @returns
 *  - `searchQuery` / `setSearchQuery`: controlled value for the search input.
 *  - `storeSearchInputRef`: ref for the input. Focus is restored after a search resolves.
 *  - `activeLocation`: the location to sort around, or `null` for the default list.
 *  - `searchError`: translated message shown when a search yields no coordinates.
 *  - `toggleGeoLocation`: first press activates the device location, pressing again clears it.
 *    A new search also replaces it.
 *  - `isGeoActive`: true while the device location is the active location.
 *  - `isLocating`: true while the device location lookup is in flight.
 */
export const useStoreLocationSearch = ({
    onLocationResolved,
}: UseStoreLocationSearchInput = {}) => {
    const { translate } = useTranslation();

    const [searchQuery, setSearchQuery] = useState('');
    const [submittedQuery, setSubmittedQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [activeLocation, setActiveLocation] = useState<StoreLocation | null>(null);
    const [isLocating, setIsLocating] = useState(false);
    const storeSearchInputRef = useRef<HTMLInputElement>(null);

    const { data: cityData, isFetching: isFetchingCoordinates } = useCitySearch(submittedQuery);

    const latitude = cityData?.[0]?.latitude;
    const longitude = cityData?.[0]?.longitude;

    // A valid postal code is complete, so there is nothing to wait for.
    const searchDebounceMs = isValidDanishPostalCode(searchQuery) ? 0 : SEARCH_DEBOUNCE_MS;

    useDebounce(
        () => {
            if (!searchQuery?.length) {
                setSubmittedQuery('');
                setSearchError('');
                // Keep an active geo location; only a search box the user emptied returns to default.
                setActiveLocation((prev) => (prev?.source === 'geo' ? prev : null));
                return;
            }

            setSubmittedQuery(searchQuery);
        },
        searchDebounceMs,
        [searchQuery],
    );

    // A submitted search always wins over an active geo location.
    useEffect(() => {
        if (!submittedQuery || isFetchingCoordinates) return;

        if (latitude !== undefined && longitude !== undefined) {
            const location: StoreLocation = {
                coordinates: { latitude, longitude },
                source: 'search',
                query: submittedQuery,
            };
            setActiveLocation(location);
            setSearchError('');
            storeSearchInputRef.current?.focus();
            onLocationResolved?.(location);
            return;
        }

        setActiveLocation(null);
        setSearchError(translate('message.noSearchResultsFound'));
        storeSearchInputRef.current?.focus();
    }, [latitude, longitude, isFetchingCoordinates, submittedQuery]);

    const toggleGeoLocation = () => {
        if (!navigator.geolocation || isLocating) return;

        // Toggling off falls back to the default store list.
        if (activeLocation?.source === 'geo') {
            setActiveLocation(null);
            return;
        }

        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            ({ coords }: GeolocationPosition) => {
                const location: StoreLocation = {
                    coordinates: { latitude: coords.latitude, longitude: coords.longitude },
                    source: 'geo',
                };
                setActiveLocation(location);
                setSearchError('');
                setSearchQuery('');
                setIsLocating(false);
                onLocationResolved?.(location);
            },
            () => setIsLocating(false),
        );
    };

    return {
        searchQuery,
        setSearchQuery,
        storeSearchInputRef,
        activeLocation,
        searchError,
        toggleGeoLocation,
        isGeoActive: activeLocation?.source === 'geo',
        isLocating,
    };
};
